import { useState } from 'react';
import { CheckCircleOutlined, CloseCircleOutlined, RedoOutlined } from '@ant-design/icons';

import { Badge, Button, Checkbox, Field, Textarea } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { ngayGioVN } from '../../../lib/dinh-dang';
import { useToast } from '../../../lib/toast';
import { HopXacNhanThaoTac } from '../../nguoi-dung/components/HopXacNhanThaoTac';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { MUC_NOP_LAI, TEN_MUC_NOP_LAI, TRANG_THAI_PHIEN } from '../constants';
import { useThamDinh } from '../hooks/useEkyc';
import { loiTheoTruong, loiTuApi } from '../nghiep-vu/cau-hinh';
import type { KetQuaThamDinh, MucNopLai, QuyetDinhThamDinh } from '../types';

const HOP: Record<QuyetDinhThamDinh, { hoi: KhoaDich; than: KhoaDich; nut: KhoaDich; xong: KhoaDich }> = {
  VERIFIED: {
    hoi: 'ekyc.thamDinh.duyetHoi', than: 'ekyc.thamDinh.duyetThan', nut: 'ekyc.thamDinh.duyet', xong: 'ekyc.thamDinh.daDuyet',
  },
  REJECTED: {
    hoi: 'ekyc.thamDinh.tuChoiHoi', than: 'ekyc.thamDinh.tuChoiThan', nut: 'ekyc.thamDinh.tuChoi', xong: 'ekyc.thamDinh.daTuChoi',
  },
  ADDITIONAL_REQUIRED: {
    hoi: 'ekyc.thamDinh.boSungHoi', than: 'ekyc.thamDinh.boSungThan', nut: 'ekyc.thamDinh.yeuCauBoSung',
    xong: 'ekyc.thamDinh.daYeuCauBoSung',
  },
};

/**
 * Ra quyết định trên hồ sơ đang chờ người — như khối "Ra quyết định" của màn v3: lý do, mục cần nộp lại, ba nút.
 *
 * - **Lý do là dòng người khiếu nại sau này sẽ đọc.** Từ chối thì bắt buộc (máy chủ cũng chặn); duyệt thì tuỳ.
 * - **"Yêu cầu bổ sung" phải nêu mục.** Bảo khách "nộp thêm" mà không nói nộp gì thì họ nộp lại đúng thứ vừa bị từ
 *   chối (`02` §7.1). Nút không xám đi khi chưa chọn mục — bấm thì lỗi nói ngay cạnh ô cần chọn.
 * - **Mỗi quyết định qua một hộp xác nhận**, nói hậu quả. Duyệt và từ chối là trạng thái kết thúc, không hoàn tác.
 *
 * Kiểm ở đây chỉ để báo sớm; máy chủ (`parseThamDinh`) là cổng thật và lỗi của nó về đúng ô theo con trỏ.
 */
export function KhoiRaQuyetDinh({ phienId }: { phienId: string }) {
  const { t } = useT();
  const dichLoi = useLoi();
  const toast = useToast();
  const thamDinh = useThamDinh(phienId);
  const [lyDo, setLyDo] = useState('');
  const [muc, setMuc] = useState<MucNopLai[]>([]);
  const [hoi, setHoi] = useState<QuyetDinhThamDinh | null>(null);
  const [daBam, setDaBam] = useState<QuyetDinhThamDinh | null>(null);

  const loiMay = loiTheoTruong(loiTuApi(thamDinh.error?.details));
  const loiLyDo = (daBam === 'REJECTED' && !lyDo.trim() ? t('ekyc.thamDinh.canLyDo') : undefined) ?? loiMay['#/note'];
  const loiMuc = (daBam === 'ADDITIONAL_REQUIRED' && muc.length === 0 ? t('ekyc.thamDinh.canMuc') : undefined)
    ?? loiMay['#/requestedItems'];
  const loiChung = thamDinh.error && !loiMay['#/note'] && !loiMay['#/requestedItems'] ? dichLoi(thamDinh.error) : null;

  const bam = (q: QuyetDinhThamDinh) => {
    setDaBam(q);
    thamDinh.reset();
    if (q === 'REJECTED' && !lyDo.trim()) return;
    if (q === 'ADDITIONAL_REQUIRED' && muc.length === 0) return;
    setHoi(q);
  };

  const gui = () => {
    if (!hoi) return;
    const q = hoi;
    thamDinh.mutate(
      { decision: q, note: lyDo.trim(), requestedItems: q === 'ADDITIONAL_REQUIRED' ? muc : [] },
      {
        onSuccess: () => {
          setHoi(null);
          toast.success(t(HOP[q].xong));
        },
        onError: () => setHoi(null),
      },
    );
  };

  return (
    <div className="space-y-4">
      <Field label={t('ekyc.thamDinh.lyDo')} hint={t('ekyc.thamDinh.lyDoGoiY')} error={loiLyDo}>
        {(p) => (
          <Textarea
            {...p}
            rows={3}
            maxLength={2000}
            value={lyDo}
            status={p['aria-invalid'] ? 'error' : undefined}
            placeholder={t('ekyc.thamDinh.lyDoMau')}
            onChange={(e) => setLyDo(e.target.value)}
          />
        )}
      </Field>

      <fieldset className="space-y-1.5">
        <legend className="text-sm font-medium text-content-primary">{t('ekyc.thamDinh.canNopLai')}</legend>
        <p className="text-xs text-content-muted">{t('ekyc.thamDinh.canNopLaiGoiY')}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {MUC_NOP_LAI.map((m) => (
            <Checkbox
              key={m}
              checked={muc.includes(m)}
              onChange={(e) => setMuc((cu) => (e.target.checked ? [...cu, m] : cu.filter((x) => x !== m)))}
            >
              {t(TEN_MUC_NOP_LAI[m])}
            </Checkbox>
          ))}
        </div>
        {loiMuc && <p role="alert" className="text-xs text-danger-700">{loiMuc}</p>}
      </fieldset>

      {loiChung && <LoiTrongHop>{loiChung}</LoiTrongHop>}

      <div className="flex flex-wrap gap-2 border-t border-default pt-4">
        <Button variant="primary" size="md" icon={<CheckCircleOutlined />} onClick={() => bam('VERIFIED')}>
          {t('ekyc.thamDinh.duyet')}
        </Button>
        <Button variant="destructive" size="md" icon={<CloseCircleOutlined />} onClick={() => bam('REJECTED')}>
          {t('ekyc.thamDinh.tuChoi')}
        </Button>
        <Button variant="outline" size="md" icon={<RedoOutlined />} onClick={() => bam('ADDITIONAL_REQUIRED')}>
          {t('ekyc.thamDinh.yeuCauBoSung')}
        </Button>
      </div>

      <HopXacNhanThaoTac
        open={hoi !== null}
        tieuDe={hoi ? t(HOP[hoi].hoi) : ''}
        than={hoi ? t(HOP[hoi].than, { muc: muc.map((m) => t(TEN_MUC_NOP_LAI[m])).join(', ') }) : ''}
        nhanXacNhan={hoi ? t(HOP[hoi].nut) : ''}
        nguyHiem={hoi === 'REJECTED'}
        dangChay={thamDinh.isPending}
        loi={null}
        onClose={() => setHoi(null)}
        onXacNhan={gui}
      />
    </div>
  );
}

/** Kết quả lần thẩm định gần nhất — hiện cho mọi người xem được hồ sơ, kể cả người không có quyền quyết. */
export function KetQuaThamDinhView({ review, daXoa }: { review: KetQuaThamDinh; daXoa: boolean }) {
  const { t } = useT();
  const tong = TRANG_THAI_PHIEN[review.decision];
  return (
    <dl className="space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={tong.variant}>{t(tong.khoa)}</Badge>
        {review.at && <span className="text-xs text-content-muted">{ngayGioVN(review.at)}</span>}
      </div>
      <div>
        <dt className="text-xs text-content-muted">{t('ekyc.thamDinh.lyDo')}</dt>
        <dd className="mt-0.5 whitespace-pre-wrap text-content-primary">
          {review.note || (daXoa ? t('ekyc.thamDinh.lyDoDaXoa') : t('ekyc.thamDinh.khongGhiLyDo'))}
        </dd>
      </div>
      {review.requestedItems.length > 0 && (
        <div>
          <dt className="text-xs text-content-muted">{t('ekyc.thamDinh.canNopLai')}</dt>
          <dd className="mt-1 flex flex-wrap gap-1.5">
            {review.requestedItems.map((m) => (
              <Badge key={m} variant="neutral">{TEN_MUC_NOP_LAI[m] ? t(TEN_MUC_NOP_LAI[m]) : m}</Badge>
            ))}
          </dd>
        </div>
      )}
    </dl>
  );
}
